import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Paper,
  Typography,
} from "@mui/material";
import CommonModal from "../../components/common/CommonModal";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { format, isBefore, parseISO, differenceInCalendarDays } from "date-fns";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useError } from "../../contexts/ErrorContext";
import {
  HomeworkSummaryDto,
  downloadMyHomeworkAttachment,
  useMyHomework,
  useMyHomeworkDetail,
} from "../../hooks/useHomework";

const formatDueDate = (value: string) => {
  if (!value) return "-";
  try {
    return format(parseISO(value), "dd MMM yyyy");
  } catch {
    return value;
  }
};

const getDueDateColor = (dueDate: string): string => {
  try {
    const due = parseISO(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (isBefore(due, today)) return "#d32f2f"; // overdue
    if (differenceInCalendarDays(due, today) <= 3) return "#ed6c02"; // due soon
  } catch {
    // ignore parse errors, fall back to default color
  }
  return "text.secondary";
};

const isOverdue = (dueDate: string): boolean => {
  try {
    const due = parseISO(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return isBefore(due, today);
  } catch {
    return false;
  }
};

const formatFileSize = (bytes: number): string => {
  if (!bytes) return "0 KB";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>
      {value ?? "-"}
    </Typography>
  </Grid>
);

const StudentHomeworkPage: React.FC = () => {
  const { t } = useTranslation();
  const { showError } = useError();
  const location = useLocation();
  const queryClient = useQueryClient();

  const listQuery = useMyHomework();

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const detailQuery = useMyHomeworkDetail(selectedId ?? undefined);

  // Deep-link from the notification bell: navigate('/student/homework', { state: { homeworkId } })
  useEffect(() => {
    const homeworkId = (location.state as { homeworkId?: number } | null)?.homeworkId;
    if (homeworkId) {
      queryClient.invalidateQueries({ queryKey: ["homework", "my"] });
      setSelectedId(homeworkId);
      setDetailOpen(true);
    }
  }, [location.state, queryClient]);

  useEffect(() => {
    if (detailQuery.isError) {
      const err = detailQuery.error as { response?: { data?: { message?: string } }; message?: string };
      showError(
        err?.response?.data?.message || t("access_denied_homework") || "You don't have access to this homework"
      );
    }
  }, [detailQuery.isError, detailQuery.error, showError, t]);

  const handleOpenDetails = (id: number) => {
    setSelectedId(id);
    setDetailOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailOpen(false);
    setSelectedId(null);
  };

  const homeworkList = listQuery.data ?? [];

  return (
    <Paper sx={{ p: 2, maxWidth: 1500, mx: "auto", mt: 1 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        {t("my_homework") || "My Homework"}
      </Typography>

      {listQuery.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : listQuery.error ? (
        <Alert severity="error">
          {listQuery.error.message || t("error_loading_homework") || "Error loading homework"}
        </Alert>
      ) : homeworkList.length === 0 ? (
        <Typography color="text.secondary">
          {t("no_homework_assigned_yet") || "No homework assigned yet."}
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {homeworkList.map((homework: HomeworkSummaryDto) => {
            const dueColor = getDueDateColor(homework.dueDate);
            const overdue = isOverdue(homework.dueDate);
            return (
              <Grid item xs={12} md={4} key={homework.id}>
                <Card variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                      <Chip label={homework.subjectName} size="small" color="primary" variant="outlined" />
                      <Chip label={homework.className} size="small" variant="outlined" />
                      {overdue && <Chip label={t("overdue") || "Overdue"} size="small" color="error" />}
                    </Box>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      {homework.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {t("assigned_by") || "Assigned by"} {homework.assignedByName}
                    </Typography>
                    <Typography variant="body2" sx={{ color: dueColor, mb: 0.5 }}>
                      {t("due_date") || "Due"}: {formatDueDate(homework.dueDate)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("date_of_assignment") || "Assigned on"}: {formatDueDate(homework.assignmentDate)}
                    </Typography>
                    {homework.attachmentCount > 0 && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1, color: "text.secondary" }}>
                        <AttachFileIcon fontSize="small" />
                        <Typography variant="caption">{homework.attachmentCount}</Typography>
                      </Box>
                    )}
                  </CardContent>
                  <CardActions sx={{ px: 2, pb: 2 }}>
                    <Button size="small" variant="outlined" onClick={() => handleOpenDetails(homework.id)}>
                      {t("view_details") || "View Details"}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <CommonModal
        open={detailOpen}
        onClose={handleCloseDetails}
        title={t("homework_details") || "Homework Details"}
        maxWidth="sm"
        fullWidth
        hideActions
      >
        {detailQuery.isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : detailQuery.isError ? (
          <Alert severity="error">
            {t("access_denied_homework") || "You don't have access to this homework"}
          </Alert>
        ) : detailQuery.data ? (
          <Box>
            <Grid container spacing={2}>
              <DetailRow label={t("session") || "Session"} value={detailQuery.data.sessionName} />
              <DetailRow label={t("class") || "Class"} value={detailQuery.data.className} />
              <DetailRow label={t("subject") || "Subject"} value={detailQuery.data.subjectName} />
              <DetailRow label={t("assigned_by") || "Assigned By"} value={detailQuery.data.assignedByName} />
              <DetailRow
                label={t("date_of_assignment") || "Date of Assignment"}
                value={formatDueDate(detailQuery.data.assignmentDate)}
              />
              <DetailRow label={t("due_date") || "Due Date"} value={formatDueDate(detailQuery.data.dueDate)} />
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="caption" color="text.secondary">
              {t("title") || "Title"}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
              {detailQuery.data.title}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              {t("description_instructions") || "Description / Instructions"}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
              {detailQuery.data.description}
            </Typography>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("attachments") || "Attachments"}
              {detailQuery.data.attachments?.length ? (
                <Chip size="small" label={detailQuery.data.attachments.length} sx={{ ml: 1 }} />
              ) : null}
            </Typography>
            {detailQuery.data.attachments && detailQuery.data.attachments.length > 0 ? (
              <List dense sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}>
                {detailQuery.data.attachments.map((att) => (
                  <ListItem
                    key={att.id}
                    secondaryAction={
                      <IconButton
                        size="small"
                        onClick={() => downloadMyHomeworkAttachment(att.fileName, att.originalFileName)}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <InsertDriveFileIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={att.originalFileName} secondary={formatFileSize(att.fileSize)} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("no_attachments") || "No attachments"}
              </Typography>
            )}
          </Box>
        ) : null}
      </CommonModal>
    </Paper>
  );
};

export default StudentHomeworkPage;
